import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import React, { Fragment, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  API_DOCUMENT_TYPES,
  API_DOSSIERS,
  API_TRIAL_PERIOD_REPORT_UPLOAD,
  API_TRIAL_PERIOD_REPORTS,
} from '../../../../constants'
import {
  DossierDb,
  MokaiDocumentType,
  UploadMask,
} from '../../../../model/dossier.model'
import { RequestMethod, Status } from '../../../../model/enums/enums'
import { ocrProposalProps } from '../../../../model/ocrProposal.model'
import { Person } from '../../../../model/person.model'
import { TrialPeriodReport } from '../../../../model/trialPeriodReport.model'
import { addDocumentType } from '../../../../redux/basic/dossier/documentTypeAction'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { updateTrialPeriodReport } from '../../../../redux/official-document/trial-period-report/trialPeriodReportActions'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { handleFileUploadByFile } from '../../../../services/api/handleFileUpload'
import { updateTrialPeriodReportInDb } from '../../../../services/api/mokaiApiService'
import {
  findApprenticeById,
  findPersonById,
} from '../../../../util/arraySearch'
import { getFormattedDateForMySQL } from '../../../../util/formatting'
import { getPersonName } from '../../../../util/getPersonName'
import { ApprenticeDropdown } from '../../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import { EmployeeDropdown } from '../../../util/form/dropdown/person-dropdown/EmployeeDropdown'
import {
  conclusionCellBody,
  creationDateCellBody,
  discussedCellBody,
  meetingDateCellBody,
} from '../../document/trial-period-report/list/TrialPeriodReportList'
import { DocumentUpload } from '../../dossier/DocumentUpload'
import { cutEnding, getDocumentType } from '../../dossier/Helpers'

export const TrialReportProposalDialog = (
  props: ocrProposalProps
): JSX.Element => {
  const apprentices = useAppSelector((state) => state.apprentices)
  const persons = useAppSelector((state) => state.employees)
  const documentTypes = useAppSelector((state) => state.documentTypes)

  const dispatcher = useAppDispatch()

  const [selectedReport, setSelectedReport] = useState<TrialPeriodReport>()
  const [currentTrialReports, setCurrentTrialReports] =
    useState<TrialPeriodReport[]>()
  const [overwriteReportConfirmDialog, setOverwriteReportConfirmDialog] =
    useState<boolean>(false)
  const [newReportConfirmDialogOpen, setNewReportConfirmDialogOpen] =
    useState<boolean>(false)
  const [currentDossier, setCurrentDossier] = useState<DossierDb[]>([])

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: props.defaultValues,
    shouldFocusError: false,
  })

  const fetchFromDB = async (personID: number) => {
    let url = `${API_DOSSIERS()}?id_person=${personID}`
    const dossier = await handleRequest<DossierDb[]>(url, RequestMethod.GET)

    setCurrentDossier(dossier.data ? dossier.data : [])
  }

  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      // here we can only change the apprentice
      if (type === 'change') {
        // apprentice id and semester are used to load and reference the report.
        // If anything else is changed it is considered to be a unsaved value the editor wants to keep.
        if (value.id_apprentice) {
          let apprentice = findApprenticeById(value.id_apprentice, apprentices)
          if (apprentice && apprentice.person_id) {
            fetchFromDB(apprentice.person_id)
          }
        }
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch])

  const fetchDocumentTypes = async () => {
    if (documentTypes.length === 0) {
      const docTypes = await handleRequest<MokaiDocumentType[]>(
        API_DOCUMENT_TYPES(),
        RequestMethod.GET
      )
      docTypes.data?.sort((a, b) =>
        a.document_type_name.localeCompare(b.document_type_name)
      )
      // but move Sonstiges to the back
      const idx = docTypes.data?.findIndex(
        (a) => a.document_type_name === 'Sonstiges'
      )
      if (idx) {
        docTypes.data?.push(docTypes.data?.splice(idx, 1)[0])
      }
      docTypes.data?.forEach((type) => dispatcher(addDocumentType(type)))
    }
  }

  // we only fetch this once
  useEffect(() => {
    // we need to do this here once for the default one

    fetchDocumentTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUploadResultCallback = async (_: number | undefined) => {
    props.toastRef.current?.show({
      severity: 'success',
      closable: true,
      summary: t<string>('ocr.upload_successful'),
      detail: t<string>('ocr.tpr_upload_successful'),
    })

    setCurrentTrialReports(undefined)
    props.closeCallback()

    return
  }

  const signedCellBody = (rowData: TrialPeriodReport) => {
    let icon
    // 0 = true & discussed
    if (rowData.is_discussed !== undefined && rowData.is_discussed === 0) {
      icon = <i className="fas fa-check" />
    }

    return (
      <Fragment>
        <span>{icon}</span>
      </Fragment>
    )
  }

  const uploadFile = async (trialReport: TrialPeriodReport) => {
    const trialReportID = trialReport.trial_period_report_id

    if (trialReportID) {
      // it does not make sense to have an uploaded report that is not discussed
      if (trialReport.is_discussed) {
        trialReport.is_discussed = 0
        dispatcher(updateTrialPeriodReport(trialReport))
        trialReport.discussion_date = getFormattedDateForMySQL(
          trialReport.discussion_date
        )
        // this is needed since created on and last change must not be sent by the frontend
        // however they are present in this trial report since it is retrieved from the backend
        trialReport.created_on = undefined //getFormattedDateForMySQL(trialReport.created_on) as any
        trialReport.last_change = undefined
        await updateTrialPeriodReportInDb(trialReportID, trialReport)
      }

      const uploadProps = {
        documentId: trialReportID,
        uploadUrl: API_TRIAL_PERIOD_REPORT_UPLOAD(trialReportID),
        handleUploadResult: handleUploadResultCallback,
      }

      if (props.file) {
        await handleFileUploadByFile(uploadProps)(props.file).catch((err) =>
          console.log(err)
        )
        trialReport.signed_file_reference = 'created'
        dispatcher(updateTrialPeriodReport(trialReport))
      }
    }
  }

  const onSubmit = async () => {
    const tpReports = await handleRequest<TrialPeriodReport[]>(
      API_TRIAL_PERIOD_REPORTS(),
      RequestMethod.GET
    )

    const trialPeriodReports = tpReports?.data?.filter(
      (e) => e.id_apprentice === methods.getValues('id_apprentice')
    )

    if (trialPeriodReports) {
      let filteredTrialReports = trialPeriodReports.filter(
        (e) => e.id_responsible === methods.getValues('id_responsible')
      )
      filteredTrialReports = filteredTrialReports.filter(
        (e) => e.passed_status !== Status.open && e.passed_status !== null
      )
      setCurrentTrialReports(filteredTrialReports)
      if (filteredTrialReports.length > 1) {
        setOverwriteReportConfirmDialog(true)
        return
      }

      if (filteredTrialReports.length === 1) {
        uploadFile(filteredTrialReports[0])
        return
      }
    }

    setNewReportConfirmDialogOpen(true)
  }

  const getNewReportBody = (): string => {
    const apprentice = findApprenticeById(
      methods.getValues('id_apprentice'),
      apprentices
    )
    const supervisor = findPersonById(
      methods.getValues('id_responsible'),
      persons
    )
    return t<string>('ocr.new_trial_report', {
      apprentice: getPersonName(apprentice),
      supervisor: getPersonName(supervisor),
    })
  }

  const getExistingReportBody = (): string => {
    const apprentice = findApprenticeById(
      methods.getValues('id_apprentice'),
      apprentices
    )
    const supervisor = findPersonById(
      methods.getValues('id_responsible'),
      persons
    )

    return t<string>('ocr.overwrite_trial_report', {
      apprentice: getPersonName(apprentice),
      supervisor: getPersonName(supervisor),
    })
  }

  const overwriteReportFooter = () => {
    return (
      <div>
        <Button
          label={t<string>('trial_period_report.upload_signed_trial_report')}
          className="p-button-danger"
          disabled={!selectedReport}
          onClick={async () => {
            // always true
            if (selectedReport) {
              await uploadFile(selectedReport)
            }
            setNewReportConfirmDialogOpen(false)
            setSelectedReport(undefined)
          }}
        />
      </div>
    )
  }

  const um: UploadMask = {
    document_name: props.file?.name ? cutEnding(props.file.name) : '',
    comment: '',
    type: props.file?.name
      ? getDocumentType(props.file.name, documentTypes)
      : 0,
  }

  const getCurrentPerson = (): Person | undefined => {
    return findApprenticeById(methods.getValues('id_apprentice'), apprentices)
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={'formgrid p-fluid grid pl-2'}>
            <div className={'col-12 formgroup-inline grid mt-1'}>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <ApprenticeDropdown />
              </div>
              <div className={'md:col-6 col-12 mb-0 pr-2'}>
                <EmployeeDropdown fieldName={'id_responsible'} />
              </div>
            </div>
            <div className={'ml-auto mt-2 mr-3 p-fluid grid gap-3'}>
              <Button
                label={t<string>('ocr.upload_signed_file')}
                type={'submit'}
                icon="fas fa-file-upload"
                className="p-button col button-height"
              />
            </div>
          </div>
        </form>
      </FormProvider>
      <Dialog
        visible={newReportConfirmDialogOpen}
        onHide={() => setNewReportConfirmDialogOpen(false)}
        header={t<string>('ocr.new_trial_report_header')}
        className={'dialog'}
      >
        {getNewReportBody()}
        <DocumentUpload
          files={[props.file]}
          person={getCurrentPerson()}
          defaultValues={[um]}
          toastRef={props.toastRef}
          currentDossier={currentDossier}
          showDeleteButton={false}
          removeFile={(_) => {
            return
          }}
          cleanupCallback={(_) => props.closeCallback()}
        />
      </Dialog>

      <Dialog
        visible={overwriteReportConfirmDialog}
        onHide={() => setOverwriteReportConfirmDialog(false)}
        header={t<string>('ocr.existing_trial_report_header')}
        footer={overwriteReportFooter()}
        className={'dialog'}
      >
        {getExistingReportBody()}
        <div className="card">
          <DataTable
            className={'datatable-reports p-datatable-sm'}
            value={currentTrialReports}
            selection={selectedReport}
            selectionMode={'single'}
            scrollHeight={'80vh'}
            onSelectionChange={(e) => setSelectedReport(e.value)}
            dataKey="trial_period_report_id"
            emptyMessage={t<string>('core.empty_message')}
            resizableColumns
            columnResizeMode="fit"
            rowHover={true}
            stripedRows
          >
            <Column selectionMode="single" />
            <Column
              field="passed_status"
              header={t<string>('trial_period_reports.conclusion')}
              body={conclusionCellBody}
              sortable
            />
            <Column
              field="created_on"
              header={t<string>('core.created_on')}
              sortable
              body={creationDateCellBody}
            />
            <Column
              field="discussion_date"
              header={t<string>('core.document.discussion')}
              sortable
              body={meetingDateCellBody}
            />
            <Column
              field="is_discussed"
              header={t<string>('core.document.discussed')}
              body={discussedCellBody}
              sortable
            />
            <Column
              field="signed_file_reference"
              header={t<string>('core.document.signed')}
              body={signedCellBody}
              sortable
            />
          </DataTable>
        </div>
      </Dialog>
    </>
  )
}